import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { TileData, Difficulty, GameStats, Rotation } from "../types";
import { DIFFICULTY_CONFIG } from "../constants/difficulty";
import { generatePuzzle } from "../utils/puzzleGenerator";
import { checkConnections, ConnectionResult } from "../utils/connectionChecker";
import { useAudioEffects } from "../../../hooks/use-audio-effects";
import clickSound from "../assets/audio/click.wav";

export type GamePhase = "idle" | "playing" | "won" | "lost";

export interface PuzzleContextValue {
  grid: TileData[][];
  liveMap: boolean[][];
  distanceMap: number[][];
  phase: GamePhase;
  clicks: number;
  timeLimit: number;
  rotateTile: (row: number, col: number) => void;
  resetGame: () => void;
  setPhase: (phase: GamePhase) => void;
  startTime: number;
  handleLose: () => void;
}

const PuzzleContext = createContext<PuzzleContextValue | undefined>(undefined);

interface PuzzleProviderProps {
  difficulty: Difficulty;
  onWin?: (stats: GameStats) => void;
  onLose?: (stats: GameStats) => void;
  autoStart?: boolean;
  children: React.ReactNode;
}

export function PuzzleProvider({
  difficulty,
  onWin,
  onLose,
  autoStart = true,
  children,
}: PuzzleProviderProps) {
  const config = DIFFICULTY_CONFIG[difficulty];

  type PuzzleState = { grid: TileData[][]; connection: ConnectionResult };

  const [puzzleState, setPuzzleState] = useState<PuzzleState>(() => {
    const grid = generatePuzzle(config.gridSize);
    const connection = checkConnections(grid);
    return { grid, connection };
  });

  const grid = puzzleState.grid;
  const connection = puzzleState.connection;

  const [phase, setPhase] = useState<GamePhase>(autoStart ? "playing" : "idle");
  const [clicks, setClicks] = useState(0);
  const startTimeRef = useRef(Date.now());
  const [startTime] = useState(Date.now());

  const { playEffect } = useAudioEffects();
  const playEffectRef = useRef(playEffect);
  playEffectRef.current = playEffect;

  const onWinRef = useRef(onWin);
  onWinRef.current = onWin;
  const onLoseRef = useRef(onLose);
  onLoseRef.current = onLose;

  const resetGame = useCallback(() => {
    const cfg = DIFFICULTY_CONFIG[difficulty];
    const newGrid = generatePuzzle(cfg.gridSize);
    const conn = checkConnections(newGrid);
    setPuzzleState({ grid: newGrid, connection: conn });
    setClicks(0);
    startTimeRef.current = Date.now();
    setPhase("playing");
  }, [difficulty]);

  const rotateTile = useCallback(
    (row: number, col: number) => {
      if (!autoStart && phase !== "playing") {
        setPhase("playing");
      }

      if (row === 0 && col === 0) return;
      if (row === config.gridSize - 1 && col === config.gridSize - 1) return;

      // Use ref so we always call the latest playEffect without stale closure
      playEffectRef.current(clickSound);

      setPuzzleState((prev) => {
        const prevGrid = prev.grid;
        const tile = prevGrid[row][col];
        if (!tile.isOnPath) return prev;

        const nextGrid = prevGrid.slice();
        const nextRow = nextGrid[row].slice();
        const newLogical = ((tile.logicalRotation + 90) % 360) as Rotation;
        nextRow[col] = {
          ...tile,
          logicalRotation: newLogical,
          displayRotation: tile.displayRotation + 90,
        };
        nextGrid[row] = nextRow;

        const conn = checkConnections(nextGrid);
        return { grid: nextGrid, connection: conn };
      });
      setClicks((c) => c + 1);
    },
    [autoStart, config.gridSize, phase],
  );

  // Check win after each grid change
  useEffect(() => {
    if (phase !== "playing") return;
    if (connection.allLive) setPhase("won");
  }, [phase, connection.allLive]);

  const handleLose = useCallback(() => {
    if (phase !== "playing") return;
    setPhase("lost");
    const elapsed = Math.round((Date.now() - startTimeRef.current) / 1000);
    onLoseRef.current?.({
      difficulty,
      totalClicks: clicks,
      timeElapsed: elapsed,
      timeRemaining: 0,
      won: false,
    });
  }, [phase, difficulty, clicks]);

  useEffect(() => {
    if (phase === "won") {
      const elapsed = Math.round((Date.now() - startTimeRef.current) / 1000);
      onWinRef.current?.({
        difficulty,
        totalClicks: clicks,
        timeElapsed: elapsed,
        timeRemaining: 0,
        won: true,
      });
    }
  }, [phase, difficulty, clicks]);

  const value = useMemo<PuzzleContextValue>(
    () => ({
      grid,
      liveMap: connection.liveMap,
      distanceMap: connection.distanceMap,
      phase,
      clicks,
      timeLimit: config.timeLimit,
      rotateTile,
      resetGame,
      setPhase,
      startTime,
      handleLose,
    }),
    [
      grid,
      connection.liveMap,
      connection.distanceMap,
      phase,
      clicks,
      config.timeLimit,
      rotateTile,
      resetGame,
      startTime,
      handleLose,
    ],
  );

  return <PuzzleContext.Provider value={value}>{children}</PuzzleContext.Provider>;
}

export function usePuzzleContext(): PuzzleContextValue {
  const ctx = useContext(PuzzleContext);
  if (!ctx) throw new Error("usePuzzleContext must be used inside PuzzleProvider");
  return ctx;
}
